import { Component, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';

/**
 * @component SelectLocationComponent
 * 
 * This component is responsible for displaying a dropdown (`ion-select`) that allows the user 
 * to choose a location. It is primarily used in the `AppComponent`, where the selected 
 * location is stored in a `BehaviorSubject` within the `LogService`. Any other component 
 * that depends on the selected location subscribes to this service to receive updates 
 * whenever the selection changes.
 * 
 * @inputs
 * - `selectedLocationName` (string): The currently selected location.
 * - `locationsRecord` (Record<string, string>): A key-value map where the keys are location 
 *   identifiers, and the values are their display names.
 * 
 * @outputs
 * - `locationChange` (EventEmitter<string>): Emits the newly selected location value 
 *   whenever the user makes a selection.
 * 
 * @lifecycle_hooks
 * - `ngOnChanges()`: Updates the `locationNames` array whenever `locationsRecord` changes.
 */
@Component({
  selector: 'app-select-location',
  templateUrl: './select-location.component.html',
  styleUrls: ['./select-location.component.css'],
})
export class SelectLocationComponent implements OnChanges {

  /** Emits the selected location whenever it changes */
  @Output() locationChange = new EventEmitter<string>();

  /** The currently selected location */
  @Input() selectedLocationName!: string;

  /** A key-value pair of location identifiers and display names */
  @Input() locationsRecord!: Record<string, string>;
  @Input() showFieldLabel: boolean = true;

  /** List of location names extracted from `locationsRecord` */
  public locationNames!: string[];

  /**
   * Lifecycle hook that runs whenever an `@Input()` property changes.
   * - When `locationsRecord` updates, it extracts the location names 
   *   and updates the `locationNames` array.
   * 
   * @param changes - Object containing the changed input properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('locationsRecord' in changes) {
      const locationsRecord = changes['locationsRecord'].currentValue;
      if (locationsRecord) {
        this.locationNames = Object.keys(locationsRecord);
      }
    }
  }

  /**
   * Handles changes in the location dropdown.
   * - Emits the new selected value to the parent component (`AppComponent`).
   * 
   * @param event - The event object from the dropdown selection
   */
  handleLocationChange(event: any): void {
    this.locationChange.emit(event.target.value);
  }
}
